import { ArrowDown, ArrowUp, Link as LinkIcon, Plus, Star, Trash, UploadSimple, X } from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDeferredValue, useEffect, useMemo, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext } from 'react-router-dom'
import type { DashboardOutletContext } from '../components/AppShell'
import { Button, CategoryBadge, ConflictDialog, Drawer, Field, Panel, StatusBadge } from '../components/common'
import { HistoryPanel } from '../components/HistoryPanel'
import { api, conflictCurrent } from '../lib/api'
import { defaultProjectStatusColor, formatUpdatedAt, projectStatuses, projectTypes, readinessStatusColors } from '../lib/constants'
import { compressImageToWebp, formatBytes, IMAGE_LIMITS, type CompressedImage } from '../lib/image-compression'
import { sortProjects, type ProjectSort, type ProjectSortKey } from '../lib/project-sort'
import type { DashboardData, Project, Subtask, WorkingAttachment, WorkingLink } from '../lib/types'

interface PendingAttachment extends CompressedImage { id: string; previewUrl: string }

export type ProjectForm = Pick<Project, 'type' | 'name' | 'groupName' | 'focus' | 'stage' | 'action' | 'description' | 'status' | 'statusColor' | 'targetDate' | 'dateNote' | 'subtasks' | 'links' | 'notes'>
const emptyForm: ProjectForm = { type: 'PowerEdge', name: '', groupName: '', focus: false, stage: 0, action: '', description: '', status: '待啟動', statusColor: 'gray', targetDate: '', dateNote: '', subtasks: [], links: [], notes: '' }
const labels = { type: '類型', name: '專案名稱', groupName: 'Project 前綴', focus: 'Focus', stage: '階段', description: '專案現況', notes: '備註', status: '狀態', statusColor: '顯示顏色', targetDate: '目標日期', dateNote: '日期備註' }
export const projectFormLabels = labels
const projectStageLabels = ['未啟動', '專案啟動與前期規劃', '物料申請與測試準備', '測試啟動與執行', '完成'] as const

function ProjectSortHeader({ label, sortKey, sort, onSort, className = '' }: {
  label: string
  sortKey: ProjectSortKey
  sort: ProjectSort | null
  onSort: (key: ProjectSortKey) => void
  className?: string
}) {
  const direction = sort?.key === sortKey ? sort.direction : null
  return <th className={className} aria-sort={direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'}>
    <button type="button" className={`working-sort-header ${direction ? 'active' : ''}`} onClick={() => onSort(sortKey)} title={`依${label}排序`}>
      {label}
      {direction === 'asc' ? <ArrowUp size={12} weight="bold" /> : direction === 'desc' ? <ArrowDown size={12} weight="bold" /> : <span className="working-sort-neutral" aria-hidden="true">↕</span>}
    </button>
  </th>
}


export function ProjectsPage() {
  const { data, canEdit, isAdmin } = useOutletContext<DashboardOutletContext>()
  const queryClient = useQueryClient()
  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const [type, setType] = useState('全部')
  const [status, setStatus] = useState('全部')
  const [priorityOnly, setPriorityOnly] = useState(true)
  const [selectedStage, setSelectedStage] = useState<number | null>(null)
  const [tableSort, setTableSort] = useState<ProjectSort | null>(null)
  const [editing, setEditing] = useState<Project | null>(null)
  const [form, setForm] = useState<ProjectForm>(emptyForm)
  const [open, setOpen] = useState(false)
  const [conflict, setConflict] = useState<Project | null>(null)
  const [message, setMessage] = useState('')
  const [pendingIds, setPendingIds] = useState<Record<string, boolean>>({})
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([])
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState<string[]>([])
  const [attachmentBusy, setAttachmentBusy] = useState(false)
  const [attachmentMessage, setAttachmentMessage] = useState('')

  const attachmentsQuery = useQuery({
    queryKey: ['project-attachments', editing?.id],
    queryFn: () => api.projectAttachments(editing!.id),
    enabled: !!editing?.id,
  })

  useEffect(() => {
    if (!message) return
    const timer = setTimeout(() => setMessage(''), 2500)
    return () => clearTimeout(timer)
  }, [message])

  const filtered = useMemo(() => sortProjects(data.projects.filter((row) => {
    const matchType = type === '全部' || row.type === type
    const matchStatus = status === '全部' || row.status === status
    const matchPriority = !priorityOnly || row.focus
    const matchStage = selectedStage === null || row.stage === selectedStage
    const text = `${row.name} ${row.groupName} ${row.description} ${row.targetDate} ${row.dateNote}`.toLowerCase()
    const matchQuery = text.includes(deferredQuery.toLowerCase())
    return matchType && matchStatus && matchPriority && matchStage && matchQuery
  }), tableSort), [data.projects, deferredQuery, priorityOnly, selectedStage, status, tableSort, type])

  const changeTableSort = (key: ProjectSortKey) => setTableSort((current) => current?.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: key === 'focus' ? 'desc' : 'asc' })

  const updateProjectInCache = (updatedProject: Project) => {
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      return { ...old, projects: old.projects.map((project) => project.id === updatedProject.id ? updatedProject : project) }
    })
  }

  const patchProjectInCache = (id: string, patch: Partial<Project>) => {
    queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
      if (!old) return old
      return { ...old, projects: old.projects.map((project) => project.id === id ? { ...project, ...patch } : project) }
    })
  }

  const queueAttachments = async (files: File[]) => {
    const existingCount = (attachmentsQuery.data?.length ?? 0) - removedAttachmentIds.length
    const available = IMAGE_LIMITS.maxAttachments - existingCount - pendingAttachments.length
    if (available <= 0) { setAttachmentMessage(`每個 Project 最多可保存 ${IMAGE_LIMITS.maxAttachments} 張圖片。`); return }
    setAttachmentBusy(true)
    setAttachmentMessage('')
    const queued: PendingAttachment[] = []
    const errors: string[] = []
    for (const file of files.slice(0, available)) {
      try {
        const compressed = await compressImageToWebp(file)
        queued.push({ ...compressed, id: crypto.randomUUID(), previewUrl: URL.createObjectURL(compressed.file) })
      } catch (error) { errors.push(error instanceof Error ? error.message : '圖片壓縮失敗。') }
    }
    setPendingAttachments((current) => [...current, ...queued])
    if (files.length > available) errors.push(`已達 ${IMAGE_LIMITS.maxAttachments} 張上限，其餘圖片未加入。`)
    setAttachmentMessage(errors[0] ?? (queued.length ? `已將 ${queued.length} 張圖片轉成 WebP，儲存時會上傳。` : ''))
    setAttachmentBusy(false)
  }

  const removePendingAttachment = (id: string) => {
    setPendingAttachments((current) => current.filter((item) => {
      if (item.id !== id) return true
      URL.revokeObjectURL(item.previewUrl)
      return false
    }))
  }

  const save = useMutation({
    mutationFn: async () => {
      const savedProject = editing
        ? await api.updateEntity<Project>('projects', editing.id, editing.version, form)
        : await api.createEntity<Project>('projects', form)
      const failedPendingIds: string[] = []
      const failedDeleteIds: string[] = []
      let uploadedCount = 0
      let deletedCount = 0
      for (const attachmentId of removedAttachmentIds) {
        try { await api.deleteProjectAttachment(savedProject.id, attachmentId); deletedCount += 1 }
        catch { failedDeleteIds.push(attachmentId) }
      }
      for (const pending of pendingAttachments) {
        try { await api.uploadProjectAttachment(savedProject.id, pending); uploadedCount += 1 }
        catch { failedPendingIds.push(pending.id) }
      }
      const startingCount = attachmentsQuery.data?.length ?? editing?.attachmentCount ?? 0
      return {
        savedProject: { ...savedProject, attachmentCount: Math.max(0, startingCount - deletedCount + uploadedCount) },
        failedPendingIds, failedDeleteIds,
      }
    },
    onSuccess: ({ savedProject, failedPendingIds, failedDeleteIds }) => {
      void queryClient.invalidateQueries({ queryKey: ['project-attachments', savedProject.id] })
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => {
        if (!old) return old
        if (editing) return { ...old, projects: old.projects.map((project) => project.id === savedProject.id ? savedProject : project) }
        return { ...old, projects: [...old.projects, savedProject] }
      })
      if (failedPendingIds.length || failedDeleteIds.length) {
        setEditing(savedProject)
        setForm(toForm(savedProject))
        setPendingAttachments((current) => current.filter((item) => {
          if (failedPendingIds.includes(item.id)) return true
          URL.revokeObjectURL(item.previewUrl)
          return false
        }))
        setRemovedAttachmentIds(failedDeleteIds)
        setAttachmentMessage(`專案已儲存，但有 ${failedPendingIds.length + failedDeleteIds.length} 張圖片處理失敗，請再儲存一次重試。`)
        return
      }
      close()
      setMessage('專案資料已儲存。')
    },
    onError: (error) => { const latest = conflictCurrent<Project>(error); latest ? setConflict(latest) : setMessage(error instanceof Error ? error.message : '儲存失敗。') }
  })

  const close = () => {
    setPendingAttachments((current) => { current.forEach((item) => URL.revokeObjectURL(item.previewUrl)); return [] })
    setRemovedAttachmentIds([])
    setAttachmentMessage('')
    setAttachmentBusy(false)
    setOpen(false); setEditing(null); setForm(emptyForm); setConflict(null)
  }
  const edit = (row: Project) => {
    setPendingAttachments([])
    setRemovedAttachmentIds([])
    setAttachmentMessage('')
    setEditing(row); setForm(toForm(row)); setOpen(true); setConflict(null)
  }


  const toggleFocus = async (row: Project) => {
    if (!canEdit || pendingIds[row.id]) return
    const focus = !row.focus
    patchProjectInCache(row.id, { focus })
    setPendingIds((prev) => ({ ...prev, [row.id]: true }))
    try {
      const updated = await api.updateEntity<Project>('projects', row.id, row.version, { focus })
      updateProjectInCache(updated)
      setMessage(focus ? `已將「${row.name}」設為重點專案。` : `已取消「${row.name}」重點標記。`)
    } catch (error) {
      patchProjectInCache(row.id, { focus: row.focus })
      const latest = conflictCurrent<Project>(error)
      if (latest) {
        setEditing(row)
        setForm(toForm(row))
        setConflict(latest)
      } else {
        setMessage(error instanceof Error ? error.message : '更新失敗。')
      }
    } finally {
      setPendingIds((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
    }
  }

  const remove = async (row: Project) => {
    if (!window.confirm(`確定刪除專案「${row.name}」？`)) return
    try {
      await api.deleteEntity('projects', row.id, row.version)
      queryClient.setQueryData<DashboardData>(['dashboard'], (old) => old ? { ...old, projects: old.projects.filter((project) => project.id !== row.id) } : old)
      setMessage(`「${row.name}」已刪除。`)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '刪除失敗。')
    }
  }

  const clearAllFilters = () => {
    setPriorityOnly(false)
    setType('全部')
    setStatus('全部')
    setSelectedStage(null)
    setQuery('')
  }

  const stages = projectStageLabels.map((_, stage) => data.projects.filter((project) => Math.min(project.stage, projectStageLabels.length - 1) === stage).length)

  return (
    <section className="page-stack">
      <div className="working-header-bar">
        <div className="working-header-title-group">
          <h1>專案追蹤 (Project Overview)</h1>
          <div className="working-stat-pills">
            <span
              className="stat-pill total clickable"
              onClick={clearAllFilters}
              title="點擊清除所有篩選"
            >
              全部 <strong>{data.projects.length}</strong>
            </span>
            <span
              className={`stat-pill blue clickable ${priorityOnly ? 'active' : ''}`}
              onClick={() => setPriorityOnly((prev) => !prev)}
              title={priorityOnly ? '點擊顯示全部專案' : '點擊僅篩選重點專案'}
            >
              <Star size={13} weight="fill" /> 重點 <strong>{data.projects.filter((row) => row.focus).length}</strong>
            </span>
            <span
              className={`stat-pill green clickable ${status === '正常' ? 'active' : ''}`}
              onClick={() => setStatus((prev) => prev === '正常' ? '全部' : '正常')}
              title="點擊篩選正常專案"
            >
              正常 <strong>{data.projects.filter((row) => row.status === '正常').length}</strong>
            </span>
            <span
              className={`stat-pill red clickable ${status === '風險' ? 'active' : ''}`}
              onClick={() => setStatus((prev) => prev === '風險' ? '全部' : '風險')}
              title="點擊篩選風險專案"
            >
              風險 <strong>{data.projects.filter((row) => row.status === '風險').length}</strong>
            </span>
          </div>
        </div>
        {canEdit ? (
          <Button variant="primary" onClick={() => { setEditing(null); setForm(emptyForm); setOpen(true) }}>
            <Plus size={15} weight="bold" />新增 Project
          </Button>
        ) : null}
      </div>
      {message ? (
        <div className="floating-toast">
          <span>{message}</span>
          <button type="button" onClick={() => setMessage('')} aria-label="關閉通知">×</button>
        </div>
      ) : null}
      <div className="stage-strip">
        <span className="stage-strip-label">階段分佈 (Stage)：</span>
        <div className="stage-strip-steps">
          {stages.map((count, index) => {
            const isSelected = selectedStage === index
            return (
              <button
                key={index}
                type="button"
                className={`stage-strip-step ${count ? 'active' : ''} ${isSelected ? 'stage-selected' : ''}`}
                onClick={() => setSelectedStage((prev) => prev === index ? null : index)}
                title={isSelected ? `點擊取消 Stage ${index} 篩選` : `點擊篩選 Stage ${index}`}
                style={{ cursor: 'pointer' }}
              >
                <span>{projectStageLabels[index]}</span>
                <strong>{count}</strong>
              </button>
            )
          })}
        </div>
      </div>
      <Panel>
        <div className="panel-toolbar">
          <div><h2>Project Flow Position</h2></div>
          <div className="filters">
            <input className="search-input" placeholder="搜尋 Project、現況或日期" value={query} onChange={(e) => setQuery(e.target.value)} />
            <button
              type="button"
              className={`priority-filter-btn ${priorityOnly ? 'active' : ''}`}
              onClick={() => setPriorityOnly((prev) => !prev)}
              title={priorityOnly ? '顯示全部專案' : '僅顯示重點專案'}
            >
              <Star size={15} weight={priorityOnly ? 'fill' : 'bold'} />
              <span>重點專案{data.projects.filter((p) => p.focus).length > 0 ? ` (${data.projects.filter((p) => p.focus).length})` : ''}</span>
            </button>
            {selectedStage !== null ? (
              <div className="owner-filter-chip">
                <span>階段: <strong>{projectStageLabels[selectedStage]}</strong></span>
                <button type="button" onClick={() => setSelectedStage(null)} aria-label="清除階段篩選">×</button>
              </div>
            ) : null}
            <select value={type} onChange={(e) => setType(e.target.value)} aria-label="篩選類型">
              <option value="全部">全部類型</option>
              {projectTypes.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="篩選狀態">
              <option value="全部">全部狀態</option>
              {projectStatuses.map((value) => <option key={value} value={value}>{value}</option>)}
            </select>
          </div>
        </div>
        <div className="table-wrap project-table">
          <table>
            <colgroup>
              <col className="project-col-priority" />
              <col className="project-col-name" />
              <col className="project-col-type" />
              <col className="project-col-flow" />
              <col className="project-col-stage" />
              <col className="project-col-description" />
              <col className="project-col-status" />
              <col className="project-col-date" />
              {isAdmin ? <col className="project-col-actions" /> : null}
            </colgroup>
            <thead>
              <tr>
                <ProjectSortHeader label="重點" sortKey="focus" sort={tableSort} onSort={changeTableSort} className="center-column" />
                <ProjectSortHeader label="Project" sortKey="name" sort={tableSort} onSort={changeTableSort} />
                <ProjectSortHeader label="Type" sortKey="type" sort={tableSort} onSort={changeTableSort} />
                <ProjectSortHeader label="流程位置" sortKey="flow" sort={tableSort} onSort={changeTableSort} />
                <ProjectSortHeader label="目前階段" sortKey="stage" sort={tableSort} onSort={changeTableSort} />
                <ProjectSortHeader label="專案現況" sortKey="description" sort={tableSort} onSort={changeTableSort} />
                <ProjectSortHeader label="Status" sortKey="status" sort={tableSort} onSort={changeTableSort} />
                <ProjectSortHeader label="Target Date" sortKey="targetDate" sort={tableSort} onSort={changeTableSort} />
                {isAdmin ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id} className={`clickable-row ${row.focus ? 'project-row-priority' : ''}`} onClick={() => canEdit && edit(row)}>
                  <td className="center-column" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`priority-star-btn ${row.focus ? 'active' : ''}`}
                      disabled={!canEdit || pendingIds[row.id]}
                      aria-label={row.focus ? `取消重點專案：${row.name}` : `設為重點專案：${row.name}`}
                      title={row.focus ? '取消重點專案' : '設為重點專案'}
                      onClick={() => void toggleFocus(row)}
                    >
                      <Star size={15} weight={row.focus ? 'fill' : 'bold'} />
                    </button>
                  </td>
                  <td>
                    <strong className="project-title">
                      {row.groupName ? <span className="project-prefix">({row.groupName})</span> : null}
                      {row.name}
                    </strong>
                  </td>
                  <td>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setType((prev) => prev === row.type ? '全部' : row.type)
                      }}
                      title={`點擊篩選類型：${row.type}`}
                    >
                      <CategoryBadge value={row.type} />
                    </button>
                  </td>
                  <td>
                    <ProjectStageFlow stage={row.stage} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`project-stage-button ${selectedStage === row.stage ? 'active' : ''}`}
                      onClick={(event) => {
                        event.stopPropagation()
                        setSelectedStage((prev) => prev === row.stage ? null : row.stage)
                      }}
                      title={selectedStage === row.stage ? `取消階段篩選：${stageLabel(row.stage)}` : `篩選階段：${stageLabel(row.stage)}`}
                    >
                      {stageLabel(row.stage)}
                    </button>
                  </td>
                  <ProjectOverviewCell description={row.description} />
                  <td>
                    <button
                      type="button"
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setStatus((prev) => prev === row.status ? '全部' : row.status)
                      }}
                      title={`點擊篩選狀態：${row.status}`}
                    >
                      <StatusBadge status={row.status} tone={row.statusColor} />
                    </button>
                  </td>
                  <td className="project-date-cell">
                    <strong>{row.targetDate || '—'}</strong>
                    {row.dateNote ? <small>{row.dateNote}</small> : null}
                  </td>
                  {isAdmin ? <td><button className="icon-button danger" aria-label="刪除" onClick={(event) => { event.stopPropagation(); void remove(row) }}><Trash size={17} /></button></td> : null}
                </tr>
              ))}
              {filtered.length === 0 ? <tr><td colSpan={isAdmin ? 9 : 8} className="table-empty">找不到符合條件的 Project。</td></tr> : null}
            </tbody>
          </table>
        </div>
      </Panel>
      <Drawer open={open} title={editing ? `編輯 Project · ${editing.name}` : '新增 Project'} subtitle={editing ? `版本 ${editing.version} · ${editing.updatedByName} · ${formatUpdatedAt(editing.updatedAt)}` : '建立新的專案追蹤項目'} onClose={close} footer={<><Button onClick={close}>取消</Button><Button variant="primary" form="project-form" type="submit" disabled={save.isPending}>{save.isPending ? '儲存中…' : '儲存'}</Button></>}>
        <ProjectFormView
          form={form}
          setForm={setForm}
          onSubmit={(event) => { event.preventDefault(); save.mutate() }}
          projectId={editing?.id}
          existingAttachments={attachmentsQuery.data}
          pendingAttachments={pendingAttachments}
          removedAttachmentIds={removedAttachmentIds}
          attachmentLoading={attachmentsQuery.isLoading}
          attachmentBusy={attachmentBusy}
          attachmentMessage={attachmentMessage}
          onQueueAttachments={queueAttachments}
          onRemoveExistingAttachment={(id) => setRemovedAttachmentIds((prev) => [...prev, id])}
          onRemovePendingAttachment={removePendingAttachment}
        />
        <HistoryPanel entity="project" id={editing?.id} />
      </Drawer>
      <ConflictDialog open={Boolean(conflict)} local={form as unknown as Record<string, unknown>} remote={(conflict ?? {}) as unknown as Record<string, unknown>} labels={labels} onClose={() => setConflict(null)} onUseLatest={() => { if (conflict) { setEditing(conflict); setForm(toForm(conflict)); setConflict(null) } }} />
    </section>
  )
}

interface ProjectFormViewProps {
  form: ProjectForm
  setForm: (value: ProjectForm) => void
  onSubmit: (event: FormEvent) => void
  formId?: string
  projectId?: string
  existingAttachments?: WorkingAttachment[]
  pendingAttachments?: PendingAttachment[]
  removedAttachmentIds?: string[]
  attachmentLoading?: boolean
  attachmentBusy?: boolean
  attachmentMessage?: string
  onQueueAttachments?: (files: File[]) => void
  onRemoveExistingAttachment?: (id: string) => void
  onRemovePendingAttachment?: (id: string) => void
}

export function ProjectFormView({
  form, setForm, onSubmit,
  formId = 'project-form',
  projectId, existingAttachments = [], pendingAttachments = [], removedAttachmentIds = [],
  attachmentLoading, attachmentBusy, attachmentMessage,
  onQueueAttachments = () => {}, onRemoveExistingAttachment = () => {}, onRemovePendingAttachment = () => {},
}: ProjectFormViewProps) {
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')
  const [newLinkTitle, setNewLinkTitle] = useState('')
  const [newLinkUrl, setNewLinkUrl] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const [preview, setPreview] = useState<{ src: string; name: string } | null>(null)

  const addSubtask = () => {
    if (!newSubtaskTitle.trim()) return
    const item: Subtask = {
      id: `st-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: newSubtaskTitle.trim(),
      done: false,
    }
    setForm({ ...form, subtasks: [...(form.subtasks || []), item] })
    setNewSubtaskTitle('')
  }

  const removeSubtask = (id: string) => {
    setForm({ ...form, subtasks: (form.subtasks || []).filter((s) => s.id !== id) })
  }

  const moveSubtask = (index: number, direction: 'up' | 'down') => {
    const list = [...(form.subtasks || [])]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= list.length) return
    const temp = list[index]
    list[index] = list[targetIndex]
    list[targetIndex] = temp
    setForm({ ...form, subtasks: list })
  }

  const addLink = () => {
    if (!newLinkUrl.trim()) return
    let url = newLinkUrl.trim()
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`
    const item: WorkingLink = {
      id: `lk-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      title: newLinkTitle.trim() || url,
      url,
    }
    setForm({ ...form, links: [...(form.links || []), item] })
    setNewLinkTitle('')
    setNewLinkUrl('')
  }

  const removeLink = (id: string) => {
    setForm({ ...form, links: (form.links || []).filter((l) => l.id !== id) })
  }

  const totalAttachments = existingAttachments.length + pendingAttachments.length

  const acceptFiles = (files: FileList | File[]) => {
    const images = [...files].filter((file) => file.type.startsWith('image/'))
    if (images.length) onQueueAttachments(images)
  }

  return (
    <form
      id={formId}
      onSubmit={onSubmit}
      className="form-grid two"
      onPaste={(event) => {
        const files = [...event.clipboardData.items]
          .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
          .map((item) => item.getAsFile())
          .filter((file): file is File => Boolean(file))
        if (files.length) {
          event.preventDefault()
          onQueueAttachments(files)
        }
      }}
    >
      <Field label="Project Type">
        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value as ProjectForm['type'] })}>
          {projectTypes.map((value) => <option key={value}>{value}</option>)}
        </select>
      </Field>
      <Field label="Status">
        <select value={form.status} onChange={(e) => { const status = e.target.value as ProjectForm['status']; setForm({ ...form, status, statusColor: defaultProjectStatusColor(status) }) }}>
          {projectStatuses.map((value) => <option key={value}>{value}</option>)}
        </select>
      </Field>
      <Field label="顯示顏色" hint="進行中預設為綠色，也可手動調整">
        <div className="readiness-color-picker" role="radiogroup" aria-label="選擇 Project 狀態顏色">
          {readinessStatusColors.map((option) => <button key={option.value} type="button" role="radio" aria-checked={form.statusColor === option.value} className={form.statusColor === option.value ? 'selected' : ''} onClick={() => setForm({ ...form, statusColor: option.value })}><span className={`readiness-color-dot ${option.value}`} />{option.label}</button>)}
        </div>
      </Field>
      <Field label="Project Name">
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </Field>
      <Field label="Project 前綴" hint="例如 Perth，清單會顯示為 (Perth) Project Name">
        <input value={form.groupName} onChange={(e) => setForm({ ...form, groupName: e.target.value })} />
      </Field>
      <Field label="目前階段">
        <select value={Math.min(form.stage, projectStageLabels.length - 1)} onChange={(e) => setForm({ ...form, stage: Number(e.target.value) })}>
          {projectStageLabels.map((label, index) => <option key={label} value={index}>{index}. {label}</option>)}
        </select>
      </Field>
      <Field label="Target Date" hint="保留自由文字，例如 2026 W38">
        <input value={form.targetDate} onChange={(e) => setForm({ ...form, targetDate: e.target.value })} />
      </Field>
      <Field label="Date Note">
        <input value={form.dateNote} onChange={(e) => setForm({ ...form, dateNote: e.target.value })} />
      </Field>
      <Field label="Focus Project">
        <label className="check-control">
          <input type="checkbox" checked={form.focus} onChange={(e) => setForm({ ...form, focus: e.target.checked })} />
          列為本週重點
        </label>
      </Field>
      <Field label="專案現況" hint="說明目前整體進度、前提條件、物料、人力與風險">
        <textarea rows={7} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="例如：目前測試條件、Schedule、Material 及風險說明" />
      </Field>

      <div className="drawer-subtasks-section">
        <div className="drawer-section-title">
          <span>工作細項清單 ({form.subtasks?.length || 0})</span>
        </div>
        {form.subtasks && form.subtasks.length > 0 ? (
          <div>
            {form.subtasks.map((st, idx) => (
              <div key={st.id} className="subtask-drawer-row">
                <input
                  type="checkbox"
                  checked={st.done}
                  onChange={(e) => {
                    const list = (form.subtasks || []).map((s) => (s.id === st.id ? { ...s, done: e.target.checked } : s))
                    setForm({ ...form, subtasks: list })
                  }}
                />
                <input
                  type="text"
                  className="subtask-title-input"
                  value={st.title}
                  onChange={(e) => {
                    const list = (form.subtasks || []).map((s) => (s.id === st.id ? { ...s, title: e.target.value } : s))
                    setForm({ ...form, subtasks: list })
                  }}
                  placeholder="細項名稱"
                />
                <div className="subtask-btn-group">
                  <button type="button" disabled={idx === 0} onClick={() => moveSubtask(idx, 'up')} title="上移">
                    <ArrowUp size={13} />
                  </button>
                  <button
                    type="button"
                    disabled={idx === (form.subtasks || []).length - 1}
                    onClick={() => moveSubtask(idx, 'down')}
                    title="下移"
                  >
                    <ArrowDown size={13} />
                  </button>
                  <button type="button" onClick={() => removeSubtask(st.id)} title="刪除">
                    <Trash size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div className="add-subtask-bar">
          <input
            value={newSubtaskTitle}
            onChange={(e) => setNewSubtaskTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addSubtask()
              }
            }}
            placeholder="新增細項（輸入後按 Enter 或點擊新增）"
          />
          <Button type="button" onClick={addSubtask}>
            <Plus size={14} />新增細項
          </Button>
        </div>
      </div>

      <div className="drawer-links-section">
        <div className="drawer-section-title">
          <span>參考連結 / 網址 ({form.links?.length || 0})</span>
        </div>
        {form.links && form.links.length > 0 ? (
          <div>
            {form.links.map((link) => (
              <div key={link.id} className="link-drawer-row">
                <LinkIcon size={14} color="#2563eb" />
                <input
                  type="text"
                  className="link-title-input"
                  value={link.title}
                  onChange={(e) => {
                    const list = (form.links || []).map((l) => (l.id === link.id ? { ...l, title: e.target.value } : l))
                    setForm({ ...form, links: list })
                  }}
                  placeholder="連結名稱"
                />
                <input
                  type="text"
                  className="link-url-input"
                  value={link.url}
                  onChange={(e) => {
                    const list = (form.links || []).map((l) => (l.id === link.id ? { ...l, url: e.target.value } : l))
                    setForm({ ...form, links: list })
                  }}
                  placeholder="https://..."
                />
                <button type="button" className="icon-button danger" onClick={() => removeLink(link.id)} title="刪除連結">
                  <Trash size={13} />
                </button>
              </div>
            ))}
          </div>
        ) : null}
        <div className="add-link-bar">
          <input
            style={{ width: '130px', flexShrink: 0 }}
            value={newLinkTitle}
            onChange={(e) => setNewLinkTitle(e.target.value)}
            placeholder="名稱（選填）"
          />
          <input
            style={{ flex: 1 }}
            value={newLinkUrl}
            onChange={(e) => setNewLinkUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addLink()
              }
            }}
            placeholder="https://..."
          />
          <Button type="button" onClick={addLink}>
            <Plus size={14} />新增連結
          </Button>
        </div>
      </div>

      {projectId && (
        <div className="drawer-attachments-section">
          <div className="drawer-section-title">
            <span>圖片附件 ({totalAttachments})</span>
            <span className="attachment-limit">最多 {IMAGE_LIMITS.maxAttachments} 張</span>
          </div>
          {attachmentLoading ? <p className="attachment-status">載入圖片附件中…</p> : null}
          {totalAttachments > 0 ? (
            <div className="attachment-grid">
              {existingAttachments.map((attachment) => {
                const src = projectId ? api.projectAttachmentUrl(projectId, attachment.id) : ''
                return (
                  <article key={attachment.id} className="attachment-card">
                    <button
                      type="button"
                      className="attachment-preview-button"
                      onClick={() => setPreview({ src, name: attachment.originalName })}
                    >
                      <img src={src} alt={attachment.caption || attachment.originalName} loading="lazy" />
                    </button>
                    <div className="attachment-card-meta">
                      <strong title={attachment.originalName}>{attachment.originalName}</strong>
                      <small>{formatBytes(attachment.storedSize)} · WebP</small>
                    </div>
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => onRemoveExistingAttachment(attachment.id)}
                      title="儲存後刪除圖片"
                      aria-label={`刪除圖片：${attachment.originalName}`}
                    >
                      <Trash size={14} />
                    </button>
                  </article>
                )
              })}
              {pendingAttachments.map((attachment) => {
                const saved = Math.max(0, attachment.originalSize - attachment.file.size)
                const savedPercent = attachment.originalSize ? Math.round((saved / attachment.originalSize) * 100) : 0
                return (
                  <article key={attachment.id} className="attachment-card pending">
                    <button
                      type="button"
                      className="attachment-preview-button"
                      onClick={() => setPreview({ src: attachment.previewUrl, name: attachment.originalName })}
                    >
                      <img src={attachment.previewUrl} alt={attachment.originalName} />
                      <span>待上傳</span>
                    </button>
                    <div className="attachment-card-meta">
                      <strong title={attachment.originalName}>{attachment.originalName}</strong>
                      <small>
                        {formatBytes(attachment.originalSize)} → {formatBytes(attachment.file.size)}
                        {savedPercent > 0 ? `（-${savedPercent}%）` : ''}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="attachment-remove"
                      onClick={() => onRemovePendingAttachment(attachment.id)}
                      title="移除圖片"
                      aria-label={`移除圖片：${attachment.originalName}`}
                    >
                      <X size={14} />
                    </button>
                  </article>
                )
              })}
            </div>
          ) : null}
          <label
            className={`attachment-dropzone ${dragActive ? 'drag-active' : ''} ${attachmentBusy ? 'busy' : ''}`}
            onDragEnter={(event) => {
              event.preventDefault()
              setDragActive(true)
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragActive(false)
            }}
            onDrop={(event) => {
              event.preventDefault()
              setDragActive(false)
              acceptFiles(event.dataTransfer.files)
            }}
          >
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              disabled={attachmentBusy || totalAttachments >= IMAGE_LIMITS.maxAttachments}
              onChange={(event) => {
                if (event.target.files) acceptFiles(event.target.files)
                event.target.value = ''
              }}
            />
            {attachmentBusy ? (
              <span>正在轉換成 WebP…</span>
            ) : (
              <>
                <UploadSimple size={20} />
                <span>貼上、拖曳圖片，或點擊選擇檔案</span>
                <small>自動縮至 1920px 並轉成 WebP</small>
              </>
            )}
          </label>
          {removedAttachmentIds.length > 0 ? <p className="attachment-status warning">儲存後將刪除 {removedAttachmentIds.length} 張圖片。</p> : null}
          {attachmentMessage ? <p className="attachment-status">{attachmentMessage}</p> : null}
          {preview
            ? createPortal(
                <div
                  className="attachment-lightbox"
                  role="dialog"
                  aria-modal="true"
                  aria-label={`圖片預覽：${preview.name}`}
                  onClick={() => setPreview(null)}
                >
                  <button
                    type="button"
                    className="attachment-lightbox-close"
                    onClick={() => setPreview(null)}
                    aria-label="關閉圖片預覽"
                  >
                    <X size={22} />
                  </button>
                  <img src={preview.src} alt={preview.name} onClick={(event) => event.stopPropagation()} />
                  <span>{preview.name}</span>
                </div>,
                document.body,
              )
            : null}
        </div>
      )}

      <Field label="備註">
        <textarea
          rows={6}
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
          placeholder="支援直接貼上網址，檢視時會自動轉為可點擊連結"
        />
      </Field>
    </form>
  )
}

function ProjectStageFlow({ stage }: { stage: number }) {
  const currentStage = Math.min(Math.max(stage, 0), projectStageLabels.length - 1)
  return (
    <div className="project-flow" aria-label={`流程位置：${projectStageLabels[currentStage]}`}>
      {projectStageLabels.map((label, index) => (
        <span key={label} className={`project-flow-step ${index < currentStage ? 'done' : ''} ${index === currentStage ? 'current' : ''}`} title={label}>
          <i />
          <small>{label}</small>
        </span>
      ))}
    </div>
  )
}

export function ProjectOverviewCell({ description }: { description: string }) {
  return <td className="project-description-cell" title={description || undefined}><span>{description || '—'}</span></td>
}

function stageLabel(stage: number) {
  return projectStageLabels[Math.min(Math.max(stage, 0), projectStageLabels.length - 1)]
}

function toForm(row: Project): ProjectForm {
  const { type, name, groupName, focus, stage, action, description, status, statusColor, targetDate, dateNote, subtasks, links, notes } = row
  return { type, name, groupName, focus, stage, action, description, status, statusColor, targetDate, dateNote, subtasks: subtasks ?? [], links: links ?? [], notes: notes ?? '' }
}
